import { useEffect, useState } from "react";
import { lokalGefuehrte } from "../dienste/vorschlaege.js";
import { datumAusZeit } from "../kern/format.js";
import { Aenderungen } from "../ui/aenderungen.jsx";
import { Btn, Leer, TextKnopf } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";

/* ================================================================
   Vorschläge — Rückmeldung für Nutzer, Prüfung für den Admin.

   Die Regeln, nach denen eine Entscheidung den Katalog verändert
   (Änderung statt Ersatz, Bezüge, Mitnahme, Folgeablehnung), stehen
   im Server in katalogpflege.js. Diese Oberfläche zeigt sie nur an,
   damit der Admin vor dem Klick weiß, was er auslöst.
   ================================================================ */

const TYP_LABEL = { wein: "Wein", winzer: "Winzer", region: "Region" };
const STATUS_LABEL = { offen: "in Prüfung", angenommen: "angenommen", abgelehnt: "abgelehnt" };
const ART_LABEL = { neu: "Aufnahme", aenderung: "Änderung" };

/* Felder, die bei einer Aufnahme schon im Kontext oder Kopf stehen. */
const BEI_AUFNAHME_AUSBLENDEN = ["name", "typ", "winzerId", "regionId", "land", "rebsortenListe"];

/* Muss zu KORREKTUR_FELDER in katalogpflege.js passen — der Server
   übernimmt nur diese. */
const KORREKTUR_FELDER = {
  region: [["name", "Name"]],
  winzer: [["name", "Name"], ["ort", "Ort"], ["beschreibung", "Beschreibung"]],
  wein: [["name", "Name"], ["appellation", "Appellation"], ["beschreibung", "Beschreibung"]],
};

const nameVon = (v) => (v.nachher && v.nachher.name) || t("(ohne Namen)");


/* — Sicht des Nutzers ————————————————————————————————————————— */

/* Lokal geführter Altbestand — und der Weg zurück in den Katalog.

   Alles, was beim Einführen des Katalogs schon dalag, wird nie
   vorgeschlagen. Wer das nicht will, findet die Erklärung dafür hier
   und nicht in einer Fehlermeldung, die nie kommt: Ohne diesen
   Abschnitt sieht man nur, dass nichts passiert. */
function Altbestand({ db, onFreigeben }) {
  const [scharf, setScharf] = useState(false);
  const lokal = lokalGefuehrte(db);
  if (lokal.gesamt === 0 && lokal.abgekoppelt === 0) return null;

  const teile = [
    lokal.wein.length ? t.plural(lokal.wein.length, "{n} Wein", "{n} Weine") : null,
    lokal.winzer.length ? t.plural(lokal.winzer.length, "{n} Winzer", "{n} Winzer") : null,
    lokal.region.length ? t.plural(lokal.region.length, "{n} Region", "{n} Regionen") : null,
  ].filter(Boolean).join(" · ");

  return (
    <>
      <div className="wl-teil-trenner" />
      <span className="wl-konto-titel">{t("Lokal geführt")} · {lokal.gesamt}</span>
      {lokal.gesamt > 0 ? (
        <>
          <p className="wl-teil-info">
            {teile} {t("werden nur bei dir geführt — weil sie beim Einführen des Katalogs schon vorhanden waren oder weil du einen Vorschlag zurückgezogen hast. Solche Angaben werden nicht vorgeschlagen. Willst du sie gemeinsam pflegen, gib sie hier frei.")}
          </p>
          <p className="wl-teil-info">
            {t("Winzer und Region eines Weins gehen dabei mit; ohne sie wäre der Eintrag im Katalog unbrauchbar. Liegt derselbe Wein dort schon unter anderer Kennung, entsteht ein zweiter Eintrag — dafür gibt es das Zusammenführen.")}
          </p>
          <div className="wl-vs-aktionen">
            <Btn variante={scharf ? "voll" : "linie"} onClick={() => (scharf ? onFreigeben() : setScharf(true))}>
              {scharf
                ? t.plural(lokal.gesamt, "Wirklich {n} Eintrag freigeben?", "Wirklich {n} Einträge freigeben?")
                : t("Für den Katalog freigeben")}
            </Btn>
            {scharf && <TextKnopf className="ist-dezent" onClick={() => setScharf(false)}>{t("Abbrechen")}</TextKnopf>}
          </div>
        </>
      ) : null}
      {lokal.abgekoppelt > 0 && (
        <p className="wl-vs-hinweis">
          {t.plural(lokal.abgekoppelt,
            "Ein weiterer Eintrag wurde einmal abgelehnt und bleibt dauerhaft lokal.",
            "{n} weitere Einträge wurden einmal abgelehnt und bleiben dauerhaft lokal.")}
        </p>
      )}
    </>
  );
}


/* Objekte, die gerade nicht vorgeschlagen werden können, weil ihr
   Winzer oder ihre Region nur lokal geführt wird. */
function Zurueckgehalten({ liste }) {
  if (!liste || liste.length === 0) return null;
  return (
    <>
      <div className="wl-teil-trenner" />
      <span className="wl-konto-titel">{t("Zurückgehalten")} · {liste.length}</span>
      <p className="wl-teil-info">
        {t("Diese Einträge verweisen auf einen Winzer oder eine Region, die nur bei dir geführt werden. Im Katalog wären sie unbrauchbar — gib den Bezug unten unter „Lokal geführt“ frei, dann gehen sie beim nächsten Abgleich mit.")}
      </p>
      <ul className="wl-vs-liste">
        {liste.slice(0, 30).map((z) => (
          <li key={z.id} className="wl-vs-zeile">
            <div className="wl-vs-kopf"><span className="wl-vs-name">{z.name || t("(ohne Namen)")}</span></div>
            <div className="wl-vs-meta">
              {t(TYP_LABEL[z.typ])} · {z.fehlt.map((f) => `${t(TYP_LABEL[f.typ])} ${f.name || t("unbekannt")}`).join(", ")}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}


/**
 * `gesehen`: Zeitpunkt, bis zu dem der Nutzer Entscheidungen schon
 * kennt — jüngere tragen die Marke „neu“.
 * `abweichend(v)`: weicht das eigene Objekt vom Katalog ab? Nur dann
 * lohnt „Katalogfassung wiederherstellen“.
 */
function MeineVorschlaege({ vorschlaege, db, namen = {}, gesehen = null, zurueckgehalten = [],
  abweichend = () => false, onZurueckziehen, onKatalogfassung, onFreigeben, onClose }) {
  const [scharf, setScharf] = useState(null);       /* Vorschlag, der auf Bestätigung wartet */
  const [laeuft, setLaeuft] = useState(null);
  const [fehler, setFehler] = useState(null);

  const offen = vorschlaege.filter((v) => v.status === "offen");
  const entschieden = vorschlaege.filter((v) => v.status !== "offen")
    .sort((a, b) => String(b.entschieden || "").localeCompare(String(a.entschieden || "")));

  const zurueckziehen = async (v) => {
    setLaeuft(v.id); setFehler(null);
    try { await onZurueckziehen(v); setScharf(null); }
    catch (e) { setFehler(t(e.message)); }
    finally { setLaeuft(null); }
  };

  const zeile = (v) => {
    const istNeu = v.status !== "offen" && gesehen && String(v.entschieden || "") > gesehen;
    return (
      <li key={v.id} className="wl-vs-zeile">
        <div className="wl-vs-kopf">
          <span className="wl-vs-name">{nameVon(v)}</span>
          {istNeu && <span className="wl-vs-neumarke">{t("neu")}</span>}
          <span className={"wl-vs-status ist-" + v.status}>{t(STATUS_LABEL[v.status])}</span>
        </div>
        <div className="wl-vs-meta">
          {t(TYP_LABEL[v.typ])} · {t(ART_LABEL[v.art] || "Änderung")} · {datumAusZeit(v.status === "offen" ? v.erstellt : v.entschieden)}
        </div>
        <Aenderungen vorher={v.art === "neu" ? null : v.vorher} nachher={v.nachher} namen={namen}
          ausblenden={v.art === "neu" ? ["name"] : []} />
        {v.begruendung && <p className="wl-vs-begruendung">{v.begruendung}</p>}
        {v.folge && !v.begruendung && (
          <p className="wl-vs-begruendung">{t("Hängt an einem abgelehnten Winzer oder einer abgelehnten Region.")}</p>
        )}

        {v.status === "offen" && onZurueckziehen && (
          <div className="wl-vs-aktionen">
            {scharf === v.id ? (
              <>
                <Btn variante="voll" disabled={laeuft === v.id} onClick={() => zurueckziehen(v)}>
                  {laeuft === v.id ? t("Einen Moment …") : t("Wirklich zurückziehen?")}
                </Btn>
                <TextKnopf className="ist-dezent" onClick={() => setScharf(null)}>{t("Abbrechen")}</TextKnopf>
              </>
            ) : (
              <TextKnopf onClick={() => setScharf(v.id)}>{t("Zurückziehen")}</TextKnopf>
            )}
          </div>
        )}
        {v.status === "offen" && scharf === v.id && (
          <p className="wl-vs-hinweis">
            {v.art === "neu"
              ? t("Der Eintrag wird danach nur bei dir geführt. Unter „Lokal geführt“ kannst du ihn später wieder freigeben.")
              : t("Dein Eintrag wird auf die Katalogfassung zurückgesetzt. Bestände, Lagerorte und Preise bleiben.")}
          </p>
        )}

        {v.status === "abgelehnt" && v.art === "neu" && (
          <p className="wl-vs-hinweis">{t("Bleibt bei dir erhalten und wird ab jetzt rein lokal geführt.")}</p>
        )}
        {v.status === "abgelehnt" && v.art !== "neu" && (
          <>
            <p className="wl-vs-hinweis">
              {t("Deine Fassung bleibt bei dir stehen; der Eintrag bleibt mit dem Katalog verbunden und bekommt weitere Änderungen mit.")}
            </p>
            {abweichend(v) && onKatalogfassung && (
              <div className="wl-vs-aktionen">
                <TextKnopf onClick={() => onKatalogfassung(v)}>{t("Katalogfassung wiederherstellen")}</TextKnopf>
              </div>
            )}
          </>
        )}
      </li>
    );
  };

  return (
    <div className="wl-teil">
      <p className="wl-teil-info">
        {t("Legst du im Cloud-Modus einen Wein, Winzer oder eine Region an oder änderst etwas daran, wird das dem Verwalter des Katalogs vorgelegt. Bis zur Entscheidung arbeitest du ungehindert mit deiner eigenen Fassung weiter.")}
      </p>
      {fehler && <p className="wl-fehler">{fehler}</p>}

      <span className="wl-konto-titel">{t("In Prüfung")} · {offen.length}</span>
      {offen.length === 0 ? <Leer text={t("Nichts offen.")} /> : <ul className="wl-vs-liste">{offen.map(zeile)}</ul>}

      {entschieden.length > 0 && (
        <>
          <div className="wl-teil-trenner" />
          <span className="wl-konto-titel">{t("Entschieden")}</span>
          <ul className="wl-vs-liste">{entschieden.slice(0, 30).map(zeile)}</ul>
        </>
      )}

      <Zurueckgehalten liste={zurueckgehalten} />
      {db && onFreigeben && <Altbestand db={db} onFreigeben={onFreigeben} />}

      <div className="wl-teil-fuss">
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}


/* — Sicht des Admins —————————————————————————————————————————— */

/* Offene Aufnahmen, die bei Ablehnung von `v` mitgehen — dieselbe
   Regel wie im Server (katalogpflege.js), hier nur zur Beschriftung. */
function folgeAblehnungen(v, offen) {
  if (v.art !== "neu" || v.typ === "wein") return [];
  const weg = new Set([v.objektId]);
  const raus = [];
  let wachsend = true;
  while (wachsend) {
    wachsend = false;
    for (const x of offen) {
      if (x.id === v.id || raus.includes(x) || x.art !== "neu") continue;
      const n = x.nachher || {};
      if (weg.has(n.regionId) || weg.has(n.winzerId)) {
        raus.push(x);
        if (x.typ !== "wein") weg.add(x.objektId);
        wachsend = true;
      }
    }
  }
  return raus;
}


/* Ergebnis einer Entscheidung in einer Zeile — mit dem, was
   mitgegangen ist, und dem, was offen geblieben ist. */
function Ergebnis({ erg }) {
  if (!erg) return null;
  const an = erg.entschieden.filter((e) => e.status === "angenommen");
  const ab = erg.entschieden.filter((e) => e.status === "abgelehnt");
  const teile = [
    an.length ? t.plural(an.length, "{n} angenommen", "{n} angenommen") : null,
    an.some((e) => e.mitgenommen) ? t("davon {n} als Bezug mitgenommen", { n: an.filter((e) => e.mitgenommen).length }) : null,
    ab.length ? t.plural(ab.length, "{n} abgelehnt", "{n} abgelehnt") : null,
    ab.some((e) => e.folge) ? t("davon {n} als Folge", { n: ab.filter((e) => e.folge).length }) : null,
  ].filter(Boolean);
  return (
    <div className="wl-vs-ergebnis">
      {teile.length > 0 && <p className="wl-vs-hinweis">{teile.join(" · ")}</p>}
      {erg.fehler.map((f) => (
        <p key={f.id || f.text} className="wl-fehler">{f.name ? `${f.name}: ` : ""}{f.text}</p>
      ))}
    </div>
  );
}


function Korrektur({ v, werte, setWerte }) {
  const felder = KORREKTUR_FELDER[v.typ] || [];
  return (
    <div className="wl-vs-korrektur">
      {felder.map(([feld, label]) => (
        <label key={feld} className="wl-vs-korrekturfeld">
          <span className="wl-vs-feld">{t(label)}</span>
          <input className="wl-eingabe"
            value={werte[feld] != null ? werte[feld] : ((v.nachher || {})[feld] || "")}
            onChange={(e) => setWerte({ ...werte, [feld]: e.target.value })} />
        </label>
      ))}
    </div>
  );
}


function AdminVorschlaege({ onClose, onGeaendert }) {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);
  const [begruendung, setBegruendung] = useState({});
  const [korrektur, setKorrektur] = useState({});      /* vorschlagId → Felder; Schlüssel vorhanden = offen */
  const [ergebnis, setErgebnis] = useState(null);
  /* Bei „Alle von … annehmen“ übergangene Vorschläge mit Dubletten-
     verdacht — sie bleiben zur Einzelprüfung offen. */
  const [uebergangen, setUebergangen] = useState(0);

  const laden = async () => {
    try {
      const r = await fetch("/api/admin/vorschlaege");
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || t("Laden fehlgeschlagen."));
      setDaten(j);
      return j;
    } catch (e) {
      setFehler(e.message);
      setDaten({ offen: [], entschieden: [], namen: {} });
      return null;
    }
  };
  useEffect(() => { laden(); }, []);

  /* Alle Entscheidungen laufen über EINEN Aufruf — auch die einzelne.
     Der Server schreibt den Katalog dabei einmal, in der richtigen
     Reihenfolge, und meldet, was offen bleiben musste. */
  const senden = async (entscheidungen, { uebergangenAnzahl = 0 } = {}) => {
    setBusy(true); setFehler(null); setErgebnis(null); setUebergangen(uebergangenAnzahl);
    try {
      const r = await fetch("/api/admin/vorschlaege/entscheidungen", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entscheidungen }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || t("Speichern fehlgeschlagen"));
      setErgebnis(j);
      for (const e of j.entschieden) {
        setKorrektur((k) => { const { [e.id]: weg, ...rest } = k; return rest; });
      }
      const neu = await laden();
      if (onGeaendert) onGeaendert(neu ? neu.offen.length : null);
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  if (!daten) return <p className="wl-teil-info">{t("Wird geladen …")}</p>;

  const namen = daten.namen || {};
  const nutzerMitOffenen = [...new Set(daten.offen.map((v) => v.benutzername).filter(Boolean))];

  const zeile = (v) => {
    const fehlt = (v.bezuege || []).filter((b) => b.stand === "fehlt");
    const wartet = (v.bezuege || []).filter((b) => b.stand === "offen");
    const folgen = folgeAblehnungen(v, daten.offen);
    const korrigiert = Object.prototype.hasOwnProperty.call(korrektur, v.id);
    const annehmen = (extra = {}) => senden([{ id: v.id, entscheidung: "angenommen",
      korrektur: korrigiert ? korrektur[v.id] : undefined, ...extra }]);

    /* Bei einer wahrscheinlichen Dublette ist Zusammenführen der
       naheliegende Weg — dann ist es der Hauptknopf. */
    const besterTreffer = v.dublette && v.aehnlich && v.aehnlich[0] && v.aehnlich[0].stufe === "sehr" ? v.aehnlich[0] : null;

    return (
      <li key={v.id} className="wl-vs-zeile">
        <div className="wl-vs-kopf">
          <span className="wl-vs-name">{nameVon(v)}</span>
          {v.dublette && <span className="wl-vs-status ist-dublette">{t("wahrscheinlich Dublette")}</span>}
          {v.veraltet && <span className="wl-vs-status ist-abgelehnt">{t("veraltet")}</span>}
          <span className="wl-vs-status ist-offen">{t(ART_LABEL[v.art] || "Änderung")}</span>
        </div>
        <div className="wl-vs-meta">
          {t(TYP_LABEL[v.typ])} · {t("von {n}", { n: v.benutzername || t("unbekannt") })} · {datumAusZeit(v.erstellt)}
        </div>

        {v.kontext && (
          <div className="wl-vs-kontext">
            {v.kontext.winzer && <span>{v.kontext.winzer}</span>}
            {v.kontext.region && <span>{v.kontext.region}{v.kontext.land ? ` · ${landName(v.kontext.land)}` : ""}</span>}
            {!v.kontext.region && v.kontext.land && <span>{landName(v.kontext.land)}</span>}
            {v.kontext.rebsorten.length > 0 && <span>{v.kontext.rebsorten.join(", ")}</span>}
            {v.kontext.imKatalog && v.art === "neu" && <span className="wl-vs-marke">{t("Bereits im Katalog")}</span>}
            {v.kontext.nutzung > 1 && <span>{t("In {n} Kellern", { n: v.kontext.nutzung })}</span>}
          </div>
        )}

        <Aenderungen vorher={v.art === "neu" ? null : v.vorher} nachher={v.nachher} namen={namen}
          ausblenden={v.art === "neu" ? BEI_AUFNAHME_AUSBLENDEN : []}
          konfliktFelder={v.konfliktFelder || []}
          leerText={v.art === "neu" ? null : t("Keine inhaltliche Änderung erkennbar.")} />

        {v.veraltet && (
          <p className="wl-vs-hinweis">
            {v.entfernt
              ? t("Der Eintrag steht nicht mehr im Katalog — vermutlich wurde er zusammengeführt. Annehmen ist nicht möglich.")
              : (v.konfliktFelder || []).length > 0
                ? t("Der Katalog wurde seit dem Vorschlag geändert. Markierte Felder hat auch jemand anderes geändert — beim Annehmen gilt dieser Vorschlag.")
                : t("Der Katalog wurde seit dem Vorschlag geändert, aber an anderen Stellen. Angenommen wird nur, was dieser Vorschlag ändert.")}
          </p>
        )}
        {wartet.map((b) => (
          <p key={b.id} className="wl-vs-hinweis">
            {t("{typ} „{name}“ wartet selbst auf Prüfung und wird beim Annehmen mitgenommen.",
              { typ: t(TYP_LABEL[b.typ]), name: b.name || t("unbekannt") })}
          </p>
        ))}
        {fehlt.map((b) => (
          <p key={b.id} className="wl-fehler">
            {t("{typ} „{name}“ fehlt im Katalog und wartet nicht als Vorschlag — so nicht annehmbar.",
              { typ: t(TYP_LABEL[b.typ]), name: b.name || t("unbekannt") })}
          </p>
        ))}

        {v.aehnlich && v.aehnlich.length > 0 && (
          <div className="wl-vs-aehnlich">
            <span className="wl-vs-feld">{t("Ähnliche Einträge im Katalog")}</span>
            <ul>
              {v.aehnlich.map((a) => (
                <li key={a.id}>
                  <span className="wl-vs-name">{a.name}</span>
                  <span className={"wl-vs-status" + (a.stufe === "sehr" ? " ist-dublette" : "")}>
                    {a.stufe === "sehr" ? t("wahrscheinlich dieselbe") : t("möglich")}
                  </span>
                  {a.neben && <span className="wl-vs-meta">{a.neben}</span>}
                  {a.merkmale && a.merkmale.length > 0 && (
                    <span className="wl-vs-merkmale">{a.merkmale.map((x) => t(x)).join(" · ")}</span>
                  )}
                  {a.jahrgaenge.length > 0 && <span className="wl-vs-meta">{a.jahrgaenge.join(", ")}</span>}
                  {a.nutzung > 0 && <span className="wl-vs-meta">{t("In {n} Kellern", { n: a.nutzung })}</span>}
                  <TextKnopf disabled={busy || fehlt.length > 0} onClick={() => annehmen({ zielId: a.id })}>
                    {t("Annehmen und zusammenführen")}
                  </TextKnopf>
                </li>
              ))}
            </ul>
          </div>
        )}

        {korrigiert && (
          <Korrektur v={v} werte={korrektur[v.id]} setWerte={(w) => setKorrektur((k) => ({ ...k, [v.id]: w }))} />
        )}

        <input className="wl-eingabe wl-vs-grund" placeholder={t("Begründung (nur bei Ablehnung sinnvoll)")}
          value={begruendung[v.id] || ""}
          onChange={(e) => setBegruendung((b) => ({ ...b, [v.id]: e.target.value }))} />
        <div className="wl-vs-aktionen wl-vs-aktionen--umbruch">
          {besterTreffer && (
            <Btn variante="voll" disabled={busy || fehlt.length > 0} onClick={() => annehmen({ zielId: besterTreffer.id })}>
              {t("Mit „{name}“ zusammenführen", { name: besterTreffer.name })}
            </Btn>
          )}
          <Btn variante={besterTreffer ? "linie" : "voll"} disabled={busy || fehlt.length > 0 || v.entfernt} onClick={() => annehmen()}>
            {besterTreffer
              ? (korrigiert ? t("Mit Korrektur trotzdem neu aufnehmen") : t("Trotzdem neu aufnehmen"))
              : (korrigiert ? t("Mit Korrektur annehmen") : t("Annehmen"))}
          </Btn>
          <Btn variante="linie" disabled={busy}
            onClick={() => senden([{ id: v.id, entscheidung: "abgelehnt", begruendung: begruendung[v.id] || null }])}>
            {folgen.length > 0 ? t("Ablehnen (+{n} abhängige)", { n: folgen.length }) : t("Ablehnen")}
          </Btn>
          <TextKnopf disabled={busy} onClick={() => setKorrektur((k) => {
            if (korrigiert) { const { [v.id]: weg, ...rest } = k; return rest; }
            return { ...k, [v.id]: {} };
          })}>
            {korrigiert ? t("Korrektur verwerfen") : t("Korrigieren")}
          </TextKnopf>
        </div>
      </li>
    );
  };

  return (
    <div className="wl-teil">
      <p className="wl-teil-info">
        {t("Angenommene Vorschläge gehen in den Katalog und stehen allen Cloud-Konten zur Verfügung. Übernommen wird nur, was der Vorschlag ändert; fehlende Winzer und Regionen gehen mit, wenn sie selbst vorgeschlagen sind. Eine abgelehnte Aufnahme bleibt beim Vorschlagenden dauerhaft lokal, eine abgelehnte Änderung nur in dieser Fassung.")}
      </p>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <Ergebnis erg={ergebnis} />
      {ergebnis && uebergangen > 0 && (
        <p className="wl-vs-hinweis">
          {t.plural(uebergangen,
            "Ein Vorschlag mit Dublettenverdacht bleibt zur Einzelprüfung offen.",
            "{n} Vorschläge mit Dublettenverdacht bleiben zur Einzelprüfung offen.")}
        </p>
      )}

      <span className="wl-konto-titel">{t("Offen")} · {daten.offen.length}</span>

      {nutzerMitOffenen.length > 0 && (
        <div className="wl-vs-sammel">
          {nutzerMitOffenen.map((n) => {
            const seine = daten.offen.filter((x) => x.benutzername === n);
            const ids = seine.map((x) => x.id);
            /* Sammelannahme übergeht Dublettenverdacht — sonst entstünde
               mit einem Klick eine Reihe doppelter Katalogeinträge. */
            const annehmbar = seine.filter((x) => !x.dublette).map((x) => x.id);
            const verdacht = ids.length - annehmbar.length;
            return (
              <span key={n} className="wl-vs-sammelpaar">
                <TextKnopf disabled={busy || annehmbar.length === 0}
                  onClick={() => senden(annehmbar.map((id) => ({ id, entscheidung: "angenommen" })), { uebergangenAnzahl: verdacht })}>
                  {verdacht > 0
                    ? t("Alle von {n} annehmen (ohne {k} mit Dublettenverdacht)", { n, k: verdacht })
                    : t("Alle von {n} annehmen", { n })}
                </TextKnopf>
                <TextKnopf className="wl-teil-widerruf" disabled={busy}
                  onClick={() => senden(ids.map((id) => ({ id, entscheidung: "abgelehnt" })))}>
                  {t("Alle von {n} ablehnen", { n })}
                </TextKnopf>
              </span>
            );
          })}
        </div>
      )}

      {daten.offen.length === 0
        ? <Leer text={t("Keine offenen Vorschläge.")} />
        : <ul className="wl-vs-liste">{daten.offen.map(zeile)}</ul>}

      {daten.entschieden.length > 0 && (
        <>
          <div className="wl-teil-trenner" />
          <span className="wl-konto-titel">{t("Zuletzt entschieden")}</span>
          <ul className="wl-vs-liste">
            {daten.entschieden.slice(0, 15).map((v) => (
              <li key={v.id} className="wl-vs-zeile">
                <div className="wl-vs-kopf">
                  <span className="wl-vs-name">{nameVon(v)}</span>
                  <span className={"wl-vs-status ist-" + v.status}>{t(STATUS_LABEL[v.status])}</span>
                </div>
                <div className="wl-vs-meta">
                  {t(TYP_LABEL[v.typ])} · {t("von {n}", { n: v.benutzername || t("unbekannt") })} · {datumAusZeit(v.entschieden)}
                </div>
                {v.begruendung && <p className="wl-vs-begruendung">{v.begruendung}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="wl-teil-fuss">
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}

export { AdminVorschlaege, MeineVorschlaege, folgeAblehnungen };
